import DataTable from "@/components/data-table";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import Spinner from "@/components/Spinner";
import { Card, CardContent } from "@/components/ui/card";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { List } from "@/components/list/List";

import { DataTableSettingsContextProvider } from "@/components/data-table/DataTableSettingsContext";
import { SuccessFetchListResponse } from "@/services/api";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { ReadProjectRole } from "@/types/projectRoleType";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";
import { PATH_PROJECT_ROLE_NEW } from "@/constants";
import { useNavigate, generatePath } from "react-router-dom";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useCheckIsProjectLocked } from "../../hooks/useCheckIsProjectLocked";

export const ProjectRoleListContent = () => {
  const tableRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { query } =
    useDataTableQuery<SuccessFetchListResponse<ReadProjectRole>>();
  const settings = useDataTableSettings<ReadProjectRole>();
  const navigate = useNavigate();
  const projectId = useProjectId();
  const { can_create } = useModulePermissions();
  const isProjectLocked = useCheckIsProjectLocked();

  const onAddRole = useCallback(() => {
    navigate(
      generatePath(PATH_PROJECT_ROLE_NEW, {
        projectId,
      }),
    );
  }, [navigate, projectId]);

  return (
    <Card>
      <CardContent className="flex grow flex-col min-h-32 w-full h-full p-1">
        {query.isLoading ? (
          <Spinner className="h-full w-full grow" label={t("table.loading")} />
        ) : (
          <>
            <DataTableSettingsContextProvider value={settings}>
              <DataTable ref={tableRef} rowClassName="cursor-pointer" />
              <List.Status className="py-3 px-2 ">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onAddRole}
                  disabled={!can_create || isProjectLocked}
                >
                  <PlusIcon className="w-4 h-4" />
                  {t("projectRoles.buttons.add")}
                </Button>
              </List.Status>
            </DataTableSettingsContextProvider>
          </>
        )}
      </CardContent>
    </Card>
  );
};
